import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { RouteCard } from "@/components/RouteCard";
import { api } from "@/lib/api";
import { CARGO_LABELS, CARGO_LIST } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search as SearchIcon, SlidersHorizontal, PackageSearch, BellPlus } from "lucide-react";
import { AlertDialog } from "@/components/AlertDialog";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [origin, setOrigin] = useState(params.get("origin") || "");
  const [destination, setDestination] = useState(params.get("destination") || "");
  const [cargoType, setCargoType] = useState(params.get("cargo_type") || "all");
  const [dateFrom, setDateFrom] = useState(params.get("date_from") || "");
  const [partial, setPartial] = useState(params.get("partial") === "true");
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRoutes = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (origin) q.set("origin", origin);
    if (destination) q.set("destination", destination);
    if (cargoType && cargoType !== "all") q.set("cargo_type", cargoType);
    if (dateFrom) q.set("date_from", dateFrom);
    if (partial) q.set("partial", "true");
    try {
      const { data } = await api.get(`/routes?${q.toString()}`);
      setRoutes(data);
    } catch { setRoutes([]); }
    finally { setLoading(false); }
  }, [origin, destination, cargoType, dateFrom, partial]);

  useEffect(() => { fetchRoutes(); /* eslint-disable-next-line */ }, []);

  const applyFilters = () => {
    const p = new URLSearchParams();
    if (origin) p.set("origin", origin);
    if (destination) p.set("destination", destination);
    if (cargoType !== "all") p.set("cargo_type", cargoType);
    if (dateFrom) p.set("date_from", dateFrom);
    if (partial) p.set("partial", "true");
    setParams(p);
    fetchRoutes();
  };

  return (
    <div className="App bg-background min-h-screen">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-10">
        <p className="label-eyebrow text-accent">Ricerca trasportatori</p>
        <h1 className="font-display font-bold tracking-tight text-3xl md:text-4xl mt-2">Trova la tratta giusta</h1>

        <div className="grid lg:grid-cols-4 gap-6 mt-8">
          {/* filters */}
          <aside className="lg:col-span-1 border border-border rounded-sm p-5 h-fit lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-4 font-display font-semibold"><SlidersHorizontal size={16} /> Filtri</div>
            <div className="space-y-4">
              <div><Label className="text-xs">Origine</Label><Input value={origin} onChange={(e) => setOrigin(e.target.value)} className="rounded-sm mt-1" data-testid="filter-origin-input" /></div>
              <div><Label className="text-xs">Destinazione</Label><Input value={destination} onChange={(e) => setDestination(e.target.value)} className="rounded-sm mt-1" data-testid="filter-destination-input" /></div>
              <div>
                <Label className="text-xs">Tipo merce</Label>
                <Select value={cargoType} onValueChange={setCargoType}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="filter-cargo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    {CARGO_LIST.map((c) => <SelectItem key={c} value={c}>{CARGO_LABELS[c]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Partenza dal</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm mt-1" data-testid="filter-date-input" /></div>
              <div className="flex items-center justify-between border border-border rounded-sm p-3">
                <span className="text-sm">Solo parziali</span>
                <Switch checked={partial} onCheckedChange={setPartial} data-testid="filter-partial-switch" />
              </div>
              <Button onClick={applyFilters} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold gap-2" data-testid="apply-filters-btn">
                <SearchIcon size={16} /> Applica filtri
              </Button>
            </div>
          </aside>

          {/* results */}
          <div className="lg:col-span-3">
            <p className="text-sm text-muted-foreground mb-4">{loading ? "Ricerca…" : `${routes.length} tratte trovate`}</p>
            {!loading && routes.length === 0 && (
              <div className="border border-dashed border-border rounded-sm p-12 text-center" data-testid="no-results">
                <PackageSearch size={40} className="mx-auto text-muted-foreground" />
                <p className="font-display font-semibold mt-4">Nessuna tratta trovata</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Crea un alert con i dettagli del trasporto: avviseremo i trasportatori con tratte compatibili e ti notificheremo appena disponibile.</p>
                <AlertDialog
                  prefill={{ origin, destination, cargo_type: cargoType, date_from: dateFrom }}
                  onCreated={() => {}}
                  trigger={
                    <Button className="rounded-sm mt-5 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold gap-2" data-testid="create-alert-btn">
                      <BellPlus size={16} /> Crea un alert di trasporto
                    </Button>
                  }
                />
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              {routes.map((r) => <RouteCard key={r.route_id} route={r} />)}
            </div>

            {!loading && routes.length > 0 && (
              <div className="mt-8 border border-border rounded-sm p-5 flex flex-col sm:flex-row items-center justify-between gap-3 bg-muted/40">
                <p className="text-sm text-muted-foreground">Non trovi la tratta giusta? Crea un alert e avvisa i trasportatori.</p>
                <AlertDialog
                  prefill={{ origin, destination, cargo_type: cargoType, date_from: dateFrom }}
                  trigger={
                    <Button variant="outline" className="rounded-sm gap-2 shrink-0" data-testid="create-alert-btn-2">
                      <BellPlus size={16} /> Crea alert
                    </Button>
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
